import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export interface FreeboxBox {
  ts: string
  source: string
  online: boolean
  wan_state: string | null
  wan_media: string | null
  ipv4: string | null
  rate_up_bytes_s: number | null
  rate_down_bytes_s: number | null
  bandwidth_up_bps: number | null
  bandwidth_down_bps: number | null
  sfp_present: boolean | null
  sfp_alim_ok: boolean | null
  sfp_has_signal: boolean | null
  sfp_has_power_report: boolean | null
  sfp_link: boolean | null
  sfp_pwr_tx_dbm: number | null
  sfp_pwr_rx_dbm: number | null
  sfp_vendor: string | null
  sfp_model: string | null
  sfp_serial: string | null
  temp_cpum_c: number | null
  temp_cpub_c: number | null
  temp_sw_c: number | null
  temp_t1_c: number | null
  temp_t2_c: number | null
  fan_rpm: number | null
  uptime_s: number | null
  firmware_version: string | null
  backup_4g: boolean
  sfp_stats?: FreeboxSfpStats
}

export interface FreeboxSfpStats {
  rx_threshold_dbm: number
  rx_min_24h: number | null
  rx_avg_24h: number | null
  rx_max_24h: number | null
  link_flaps_24h: number
  backup_pct_24h: number | null
  backup_pct_7d: number | null
}

export interface FreeboxLatest {
  boxes: FreeboxBox[]
}

export interface FreeboxSeries {
  range: string
  bucket_seconds: number
  points: {
    b: string
    rate_down_bytes_s: number | null
    rate_up_bytes_s: number | null
    sfp_pwr_rx_dbm: number | null
    sfp_pwr_tx_dbm: number | null
    temp_cpub_c: number | null
    temp_t1_c: number | null
    temp_t2_c: number | null
    fan_rpm: number | null
  }[]
}

export function useFreebox() {
  return useQuery<FreeboxLatest>({
    queryKey: ['freebox'],
    queryFn: () => api<FreeboxLatest>('/api/metrics/freebox'),
    refetchInterval: 30_000,
  })
}

export function useFreeboxSeries(range = '24h') {
  return useQuery<FreeboxSeries>({
    queryKey: ['freebox-series', range],
    queryFn: () => api<FreeboxSeries>(`/api/metrics/freebox/series?range=${range}`),
    refetchInterval: 60_000,
  })
}

export function mbps(bytesPerSec: number | null | undefined): string {
  if (bytesPerSec == null) return '—'
  const mbit = (bytesPerSec * 8) / 1_000_000
  return mbit >= 100 ? `${mbit.toFixed(0)} Mb/s` : `${mbit.toFixed(1)} Mb/s`
}

export function dbm(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(1)} dBm`
}

export function uptime(s: number | null | undefined): string {
  if (s == null) return '—'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  return d > 0 ? `${d}j ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`
}

export interface FreeboxEvent {
  ts: string
  source: string
  kind: 'media' | 'sfp_link' | 'firmware' | 'reboot'
  from: string
  to: string
}

export interface FreeboxEvents {
  range: string
  events: FreeboxEvent[]
}

export interface FreeboxLanHostRow {
  mac: string
  source: string
  name: string | null
  vendor: string | null
  host_type: string | null
  active: boolean
  reachable: boolean
  last_activity: string | null
  ips: string[]
  first_seen: string
  last_seen: string
}

export function useFreeboxEvents(range = '7d') {
  return useQuery<FreeboxEvents>({
    queryKey: ['freebox-events', range],
    queryFn: () => api<FreeboxEvents>(`/api/metrics/freebox/events?range=${range}`),
    // 90d = a wide lag-window scan server-side; poll it gently.
    refetchInterval: range === '90d' ? 300_000 : 60_000,
  })
}

export function useFreeboxLanHosts() {
  return useQuery<{ hosts: FreeboxLanHostRow[] }>({
    queryKey: ['freebox-lan-hosts'],
    queryFn: () => api<{ hosts: FreeboxLanHostRow[] }>('/api/metrics/freebox/lan-hosts'),
    refetchInterval: 60_000,
  })
}

export interface FreeboxWifiBss {
  bss_id: string
  source: string
  band: string | null
  state: string | null
  ssid: string | null
  enabled: boolean | null
  sta_count: number | null
  ts: string
}

export interface FreeboxWifi {
  range: string
  bucket_seconds: number
  bss: FreeboxWifiBss[]
  points: { b: string; sta_count_2g4: number | null; sta_count_5g: number | null }[]
}

export function useFreeboxWifi(range = '24h') {
  return useQuery<FreeboxWifi>({
    queryKey: ['freebox-wifi', range],
    queryFn: () => api<FreeboxWifi>(`/api/metrics/freebox/wifi?range=${range}`),
    refetchInterval: 60_000,
  })
}
